import { connect } from 'react-redux';
import { get } from 'lodash';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';

import { CONSUMPTION_FREQUENCY } from '@admin/commons/constants/nonSalesConsumption';

// Commons
import { Button, ListView } from '@commons/utils/styledLibraryComponents';
import { getClientStoreNameTranslation } from '@commons/utils/translations';
import { getTheme } from '@commons/utils/theme';
import EmptyState from '@commons/EmptyState';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';
import UpgradePlanOverlay from '@commons/UpgradePlanOverlay';

// Actions
import { loading, loadingSuccess } from '@actions/loading';
import { openGenericModal, refreshGenericModal } from '@actions/modal';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

// Selectors
import { getClientInfo } from '@selectors/client';
import { getPrevHorizon } from '@selectors/featureProps';

// Services
import { ingredient as ingredientService } from '@services/ingredient';

// Utils
import { exportStoreConfigXLS } from './utils/export';
import { FAKE_DATA_FOR_PLAN_UPGRADE, getActions, getColumns } from './utils/utils';
import { getBufferStockModalConfig, useBufferStockModal } from './utils/bufferStockModal';
import {
  getNonSalesConsumptionModalConfig,
  useNonSalesConsumptionModal,
} from './utils/nonSalesConsumptionModal';

import { Container, HeaderContainer, ListContainer, TitleContainer } from './styledComponents';

const IngredientBufferStocks = (props) => {
  const {
    ingredient,
    client: { hasCentralKitchens, storeName },
    user: { hasForecasts },
    currency,
    showErrorMessage,
    showSuccessMessage,
    openGenericModal,
    refreshGenericModal,
    pageLoading,
    pageLoaded,
    prevHorizon,
  } = props;

  const theme = getTheme();
  const [actions, setActions] = useState([]);

  const clientStoreNameSingular = getClientStoreNameTranslation(storeName);
  const clientStoreNamePlural = getClientStoreNameTranslation(storeName, true);

  const [columns, setColumns] = useState(getColumns(hasCentralKitchens, false));
  const [associatedStores, setAssociatedStores] = useState([]);

  // Modal props
  const { nbDays, setNbDays, bufferStockStores, setBufferStockStores } = useBufferStockModal();

  const {
    consumptionFrequency,
    setConsumptionFrequency,
    consumptionQuantity,
    setConsumptionQuantity,
    nonSalesConsumptionStores,
    setNonSalesConsumptionStores,
  } = useNonSalesConsumptionModal();

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getAssociatedStores();
  }, []);

  // Buffer Stock modal refresh
  useEffect(() => {
    const isModalOpened = get(props, 'modal.GenericModalBool', false);

    if (!isModalOpened) {
      return;
    }

    const config = getBufferStockModalConfig({
      handleBufferStockModalSave,
      nbDays,
      setNbDays,
      bufferStockStores,
      setBufferStockStores,
      associatedStores,
      storeName: clientStoreNamePlural,
      prevHorizon,
    });

    refreshGenericModal(config);
  }, [nbDays, bufferStockStores]);

  // Non-Sales Consumption modal refresh
  useEffect(() => {
    const isModalOpened = get(props, 'modal.GenericModalBool', false);

    if (!isModalOpened) {
      return;
    }

    const config = getNonSalesConsumptionModalConfig({
      handleNonSalesConsumptionModalSave,
      consumptionFrequency,
      setConsumptionFrequency,
      consumptionQuantity,
      setConsumptionQuantity,
      nonSalesConsumptionStores,
      setNonSalesConsumptionStores,
      associatedStores,
      storeName: clientStoreNamePlural,
      ingredientUnit: ingredient.unit,
      currency: currency.alphabeticCode,
    });

    refreshGenericModal(config);
  }, [consumptionFrequency, consumptionQuantity, nonSalesConsumptionStores]);

  useEffect(() => {
    setActions(
      getActions({
        openBufferStockModal,
        openNonSalesConsumptionModal,
        exportStoreConfigXLS,
        storeName: clientStoreNamePlural,
        associatedStores,
        hasCentralKitchens,
        currency: currency.alphabeticCode,
      }),
    );
  }, [
    associatedStores,
    bufferStockStores,
    nbDays,
    consumptionFrequency,
    consumptionQuantity,
    nonSalesConsumptionStores,
  ]);

  const getAssociatedStores = async () => {
    setIsLoading(true);

    if (!hasForecasts) {
      setAssociatedStores(FAKE_DATA_FOR_PLAN_UPGRADE);
      setColumns(getColumns(hasCentralKitchens, true)); //  has groups
      setIsLoading(false);
      return;
    }

    try {
      const storesConfig = await ingredientService.getStoreIngredientConfig(ingredient.id);

      const formattedStoresBufferStocks = storesConfig.map((config) => ({
        ...config,
        id: config.storeId,
        ingredientUnit: ingredient.unit,
      }));

      setBufferStockStores(formattedStoresBufferStocks);
      setNonSalesConsumptionStores(formattedStoresBufferStocks);
      setAssociatedStores(formattedStoresBufferStocks);

      const hasGroups = formattedStoresBufferStocks.some(({ groups }) => groups && groups.length);

      setColumns(getColumns(hasCentralKitchens, hasGroups, currency.alphabeticCode));
    } catch {
      setAssociatedStores([]);
      showErrorMessage(
        i18next.t('ADMIN.INGREDIENTS.ASSOCIATED_STORES_FETCH_ERROR', {
          storeName: clientStoreNamePlural.toLowerCase(),
        }),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleBufferStockModalSave = async () => {
    pageLoading();

    const storeIds = bufferStockStores.map(({ id }) => id);
    try {
      await ingredientService.upsertBufferStocks(storeIds, ingredient.id, nbDays);

      showSuccessMessage(i18next.t('ADMIN.INGREDIENTS.BUFFER_STOCKS_UPDATE_SUCCESS'));

      await getAssociatedStores();
    } catch {
      showErrorMessage(i18next.t('ADMIN.STORES.UPDATE_BUFFER_STOCK_ERROR'));
    } finally {
      setNbDays(null);
      pageLoaded();
    }
  };

  const handleNonSalesConsumptionModalSave = async () => {
    pageLoading();

    const storeIds = nonSalesConsumptionStores.map(({ id }) => id);
    try {
      await ingredientService.upsertNonSalesConsumption(
        ingredient.id,
        storeIds,
        consumptionFrequency,
        consumptionQuantity,
      );

      showSuccessMessage(i18next.t('ADMIN.STORES.THEORETICAL_CONSUMPTION_UPDATE_SUCCESS'));

      await getAssociatedStores();
    } catch {
      showErrorMessage(i18next.t('ADMIN.STORES.THEORETICAL_CONSUMPTION_UPDATE_ERROR'));
    } finally {
      setConsumptionFrequency(CONSUMPTION_FREQUENCY.DAILY);
      setConsumptionQuantity(null);
      setNonSalesConsumptionStores(associatedStores);
      pageLoaded();
    }
  };

  const openBufferStockModal = () => {
    const config = getBufferStockModalConfig({
      handleBufferStockModalSave,
      nbDays,
      setNbDays,
      bufferStockStores,
      setBufferStockStores,
      associatedStores,
      storeName: clientStoreNamePlural,
      prevHorizon,
    });

    openGenericModal(config);
  };

  const openNonSalesConsumptionModal = () => {
    const config = getNonSalesConsumptionModalConfig({
      handleNonSalesConsumptionModalSave,
      consumptionFrequency,
      setConsumptionFrequency,
      consumptionQuantity,
      setConsumptionQuantity,
      nonSalesConsumptionStores,
      setNonSalesConsumptionStores,
      associatedStores,
      storeName: clientStoreNamePlural,
      ingredientUnit: ingredient.unit,
      currency: currency.alphabeticCode,
    });

    openGenericModal(config);
  };

  if (hasForecasts && !isLoading && !associatedStores.length) {
    return (
      <Container>
        <EmptyState
          backgroundColor={theme.colors.greys.lightest}
          label={i18next.t('ADMIN.INGREDIENTS.BUFFER_STOCK_EMPTY_STATE_TITLE', {
            storeName: clientStoreNameSingular,
          })}
          labelColor={ENUM_COLORS.IP_BLACK_1}
          labelFont={ENUM_FONTS.H2_INTER}
          subtitle={i18next.t('ADMIN.INGREDIENTS.BUFFER_STOCK_EMPTY_STATE_SUBTITLE', {
            storeName: clientStoreNamePlural,
          })}
          subtitleMargin={'8px'}
        />
      </Container>
    );
  }

  return (
    <Container>
      {!hasForecasts && <UpgradePlanOverlay />}

      <ListContainer hasForecasts={hasForecasts}>
        <HeaderContainer>
          <TitleContainer>
            <Text font={ENUM_FONTS.H2}>
              {i18next.t('ADMIN.INGREDIENTS.ASSOCIATED_STORE_WHITECARD_TITLE', {
                storeName: clientStoreNamePlural,
              })}
            </Text>
            <Text font={ENUM_FONTS.H2}>{associatedStores.length}</Text>
          </TitleContainer>
          <Button
            actions={actions}
            color={'inpulse-outline'}
            iconDropdown={'/images/inpulse/carret-black.svg'}
            iconOnLeft={false}
            label={i18next.t('GENERAL.ACTIONS')}
          />
        </HeaderContainer>
        <ListView
          columns={columns}
          data={associatedStores}
          defaultOrderBy={'nbDays'}
          defaultOrderType={'desc'}
          isLoading={isLoading}
          renderEmptyState={() => <EmptyState backgroundColor={theme.colors.greys.lightest} />}
          disableFooter
          hideSearchbar
        />
      </ListContainer>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  currency: state.baseReducer.currency,
  client: getClientInfo(state.baseReducer.user),
  user: state.baseReducer.user,
  modal: state.modalReducer,
  prevHorizon: getPrevHorizon(state.baseReducer.userRights),
});

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
  refreshGenericModal: (params) => {
    dispatch(refreshGenericModal(params));
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(IngredientBufferStocks);
